import {Platform} from 'react-native';
import * as VideoThumbnails from 'expo-video-thumbnails';
import * as ImageManipulator from 'expo-image-manipulator';

const MAX_PREVIEW_WIDTH = 1280;
const VIDEO_THUMBNAIL_TIMEOUT = 15_000;

function generateWebImagePreview(fileUri: string): Promise<string | undefined> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const scale = Math.min(1, MAX_PREVIEW_WIDTH / img.width);
                const width = Math.round(img.width * scale);
                const height = Math.round(img.height * scale);

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(undefined);
                    return;
                }
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(URL.createObjectURL(blob));
                        } else {
                            resolve(undefined);
                        }
                    },
                    'image/jpeg',
                    0.8,
                );
            } catch {
                resolve(undefined);
            }
        };
        img.onerror = () => resolve(undefined);
        img.src = fileUri;
    });
}

function generateWebVideoPreview(fileUri: string): Promise<string | undefined> {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.preload = 'auto';

        const timeout = setTimeout(() => {
            cleanup();
            resolve(undefined);
        }, VIDEO_THUMBNAIL_TIMEOUT);

        const cleanup = () => {
            clearTimeout(timeout);
            video.removeAttribute('src');
            video.load();
        };

        video.onloadedmetadata = () => {
            const seekTime = Math.min(5, video.duration * 0.1);
            video.currentTime = seekTime;
        };

        video.onseeked = () => {
            try {
                const scale = Math.min(1, MAX_PREVIEW_WIDTH / video.videoWidth);
                const width = Math.round(video.videoWidth * scale);
                const height = Math.round(video.videoHeight * scale);

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    cleanup();
                    resolve(undefined);
                    return;
                }
                ctx.drawImage(video, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        cleanup();
                        if (blob) {
                            resolve(URL.createObjectURL(blob));
                        } else {
                            resolve(undefined);
                        }
                    },
                    'image/jpeg',
                    0.8,
                );
            } catch {
                cleanup();
                resolve(undefined);
            }
        };

        video.onerror = () => {
            cleanup();
            resolve(undefined);
        };

        video.src = fileUri;
    });
}

export async function generatePreviewImage(
    fileUri: string,
    mimeType: string,
): Promise<string | undefined> {
    if (Platform.OS === 'web') {
        try {
            if (mimeType.startsWith('video/')) {
                return await generateWebVideoPreview(fileUri);
            }
            if (mimeType.startsWith('image/')) {
                return await generateWebImagePreview(fileUri);
            }
        } catch (err) {
            console.warn('Web preview image generation failed:', err);
        }
        return undefined;
    }

    try {
        if (mimeType.startsWith('video/')) {
            const {uri} = await VideoThumbnails.getThumbnailAsync(fileUri, {time: 5000});
            return uri;
        }

        if (mimeType.startsWith('image/')) {
            const result = await ImageManipulator.manipulateAsync(
                fileUri,
                [{resize: {width: MAX_PREVIEW_WIDTH}}],
                {compress: 0.8, format: ImageManipulator.SaveFormat.JPEG},
            );
            return result.uri;
        }
    } catch (err) {
        console.warn('Preview image generation failed:', err);
    }

    return undefined;
}
